/**
 * InjuryTimeline — shared vertical timeline used both by PlayerProfile (staff)
 * and PublicPlayerPage (families). Sorts injuries active-first, then by
 * date_start descending. In public mode hides medical notes and description.
 */

const TYPE_LABEL = {
  muscular: "Muscular", articular: "Articular", osea: "Ósea",
  contusion: "Contusión", otro: "Otro",
};

function sortInjuries(injuries) {
  return [...injuries].sort((a, b) => {
    if (a.status === "active" && b.status !== "active") return -1;
    if (b.status === "active" && a.status !== "active") return 1;
    return (b.date_start || "").localeCompare(a.date_start || "");
  });
}

function daysBetween(from, to) {
  if (!from) return null;
  const end = to ? new Date(to) : new Date();
  const start = new Date(from);
  return Math.max(0, Math.floor((end - start) / 86400000));
}

export default function InjuryTimeline({ injuries, mode = "staff", testid }) {
  const isPublic = mode === "public";
  const sorted = sortInjuries(injuries);
  return (
    <div className="relative pl-8 pr-4 py-4" data-testid={testid}>
      <div className="absolute left-4 top-4 bottom-4 w-px bg-border" aria-hidden />
      {sorted.map((inj, idx) => {
        const days = daysBetween(inj.date_start, inj.date_end);
        const sevColor = inj.severity === "grave"
          ? "border-destructive text-destructive"
          : inj.severity === "moderada"
            ? "border-orange-400 text-orange-400"
            : "border-amber-400 text-amber-400";
        const typeLabel = TYPE_LABEL[inj.type] || inj.type;
        return (
          <div key={inj.id || idx} data-testid={`injury-tl-${inj.id || idx}`} className="relative pb-4 last:pb-0">
            <span className={`absolute -left-[19px] top-1 w-2.5 h-2.5 border-2 ${inj.status === "active" ? "bg-destructive border-destructive animate-pulse" : "bg-emerald-500 border-emerald-500"}`} aria-hidden />
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-heading font-bold uppercase tracking-tight text-sm">{typeLabel}</span>
              <span className={`px-1.5 py-0.5 border text-[10px] font-mono ${sevColor}`}>{inj.severity}</span>
              {inj.status === "active" ? (
                <span className="px-1.5 py-0.5 border border-destructive text-destructive text-[9px] font-heading tracking-widest">ACTIVA</span>
              ) : (
                <span className="px-1.5 py-0.5 border border-emerald-500/60 text-emerald-400 text-[9px] font-heading tracking-widest">RECUP.</span>
              )}
              {days !== null && (
                <span className="text-[10px] font-mono text-muted-foreground">· {days} día{days === 1 ? "" : "s"} {inj.status === "active" ? "de baja" : "recuperación"}</span>
              )}
            </div>
            <div className="text-[11px] font-mono text-muted-foreground mt-0.5">
              {inj.date_start}{inj.date_end ? ` → ${inj.date_end}` : " → en curso"}
              {!isPublic && inj.minute !== null && inj.minute !== undefined && <span> · min {inj.minute}</span>}
            </div>
            {!isPublic && inj.description && <div className="text-xs mt-1">{inj.description}</div>}
            {!isPublic && inj.notes && <div className="text-[11px] text-muted-foreground mt-1 italic">{inj.notes}</div>}
          </div>
        );
      })}
    </div>
  );
}
